import { Button, Empty, Input, Popconfirm, Space, Tag, Tooltip, Typography } from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  HolderOutlined,
  PlusOutlined
} from "@ant-design/icons";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";

import { MAX_DEPTH, sourceTypeTag } from "./structure";
import styles from "./DocumentBuilder.module.css";

const { Text } = Typography;

export const sectionDropId = (sectionId) => `section::${sectionId}`;

const ContentBlock = ({ block, onRemove, onMove }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging, transform, transition } =
    useSortable({ id: block.id, data: { kind: "block", blockId: block.id } });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.4 : 1
  };

  const tag = sourceTypeTag(block);

  return (
    <div ref={setNodeRef} style={style} className={styles.block} {...attributes}>
      <span ref={setActivatorNodeRef} {...listeners} className={styles.blockGrab}>
        <HolderOutlined className={styles.grip} />
      </span>
      <Tag color={tag.color} className={styles.levelTag}>
        {tag.label}
      </Tag>
      <div className={styles.blockText}>
        <Text ellipsis className={styles.blockLabel}>
          {block.label}
        </Text>
        <Text type="secondary" className={styles.blockSource}>
          {block.filename}
        </Text>
      </div>
      <Space size={0}>
        <Tooltip title="Move up">
          <Button type="text" size="small" icon={<ArrowUpOutlined />} onClick={() => onMove(block.id, -1)} />
        </Tooltip>
        <Tooltip title="Move down">
          <Button type="text" size="small" icon={<ArrowDownOutlined />} onClick={() => onMove(block.id, 1)} />
        </Tooltip>
        <Tooltip title="Remove">
          <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => onRemove(block.id)} />
        </Tooltip>
      </Space>
    </div>
  );
};

const Section = ({ section, depth, selectedId, actions }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: sectionDropId(section.id),
    data: { kind: "section", sectionId: section.id }
  });

  const isSelected = selectedId === section.id;

  return (
    <div className={styles.section} style={{ marginLeft: (depth - 1) * 16 }}>
      <div
        className={[styles.sectionHeader, isSelected ? styles.sectionSelected : ""].join(" ")}
        onClick={() => actions.onSelect(section.id)}
      >
        <Tag className={styles.levelTag}>H{Math.min(depth, MAX_DEPTH)}</Tag>
        <Input
          size="small"
          variant="borderless"
          value={section.title}
          placeholder="Untitled section"
          className={styles.sectionTitle}
          onChange={(event) => actions.onRename(section.id, event.target.value)}
          onClick={(event) => event.stopPropagation()}
        />
        <Space size={0}>
          {depth < MAX_DEPTH && (
            <Tooltip title="Add sub-section">
              <Button
                type="text"
                size="small"
                icon={<PlusOutlined />}
                onClick={(event) => {
                  event.stopPropagation();
                  actions.onAddChild(section.id);
                }}
              />
            </Tooltip>
          )}
          <Tooltip title="Move up">
            <Button
              type="text"
              size="small"
              icon={<ArrowUpOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                actions.onMove(section.id, -1);
              }}
            />
          </Tooltip>
          <Tooltip title="Move down">
            <Button
              type="text"
              size="small"
              icon={<ArrowDownOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                actions.onMove(section.id, 1);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Remove this section?"
            description="Its sub-sections and content go with it."
            okText="Remove"
            cancelText="Keep"
            onConfirm={() => actions.onRemove(section.id)}
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={(event) => event.stopPropagation()}
            />
          </Popconfirm>
        </Space>
      </div>

      <div ref={setNodeRef} className={[styles.dropZone, isOver ? styles.dropZoneActive : ""].join(" ")}>
        <SortableContext items={section.blocks.map((block) => block.id)} strategy={verticalListSortingStrategy}>
          {section.blocks.map((block) => (
            <ContentBlock key={block.id} block={block} onRemove={actions.onRemoveBlock} onMove={actions.onMoveBlock} />
          ))}
        </SortableContext>

        {section.blocks.length === 0 && (
          <Text type="secondary" className={styles.dropHint}>
            Drop content here, or use the + buttons in Available content
          </Text>
        )}
      </div>

      {section.children.map((child) => (
        <Section key={child.id} section={child} depth={depth + 1} selectedId={selectedId} actions={actions} />
      ))}
    </div>
  );
};

/**
 * Middle panel, lower half: the structure of the document being assembled.
 * The headings are the user's own - only the content beneath a source section
 * is copied, never its heading - so a section is named here and then filled
 * with content from any of the loaded documents.
 */
const StructurePanel = ({ sections, selectedId, actions }) => {
  if (!sections.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <Text type="secondary" style={{ fontSize: 12 }}>
            Add a section, then drop content into it from Available content
          </Text>
        }
      >
        <Space>
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => actions.onAddChild(null)}>
            Add section
          </Button>
          <Button size="small" onClick={actions.onLoadOutline}>
            Load PSMF outline
          </Button>
        </Space>
      </Empty>
    );
  }

  return (
    <div className={styles.structure}>
      {sections.map((section) => (
        <Section key={section.id} section={section} depth={1} selectedId={selectedId} actions={actions} />
      ))}
    </div>
  );
};

export default StructurePanel;
